import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Segment,
  Header,
  Button,
  Icon,
  Message,
  Form,
  Input,
  TextArea,
  Dropdown,
  Table,
  Label,
  Confirm,
  Accordion,
  Divider,
  Checkbox
} from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { quizService, CreateQuizDto, QuizDto, QuestionType, CreateQuestionDto, CreateQuestionOptionDto, CreateQuizRuleDto } from '../../../services/learning/QuizService';
import { teamService, TeamListDto } from '../../../services/teams/teamService';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../hooks/useToast';

interface QuestionForm {
  id?: string;
  text: string;
  type: QuestionType;
  points: number;
  order: number;
  options: OptionForm[];
}

interface OptionForm {
  id?: string; // Opcional porque novas opções não têm id ainda
  text: string;
  isCorrect: boolean;
}

const QuizFormPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const isEditMode = !!quizId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [minimumScore, setMinimumScore] = useState(70);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | undefined>(undefined);
  const [isActive, setIsActive] = useState(true);
  const [questions, setQuestions] = useState<QuestionForm[]>([]);
  const [rules, setRules] = useState<CreateQuizRuleDto[]>([]);
  const [trainingTeamIds, setTrainingTeamIds] = useState<string[]>([]);
  const [availableTrainingTeams, setAvailableTrainingTeams] = useState<TeamListDto[]>([]);

  const questionTypeOptions = [
    { key: QuestionType.MultipleChoice, value: QuestionType.MultipleChoice, text: 'Múltipla Escolha' },
    { key: QuestionType.TrueFalse, value: QuestionType.TrueFalse, text: 'Verdadeiro/Falso' }
  ];

  const categoryOptions = [
    { key: 'Introdução', value: 'Introdução', text: 'Introdução' },
    { key: 'Contratos', value: 'Contratos', text: 'Contratos' },
    { key: 'Clientes', value: 'Clientes', text: 'Clientes' },
    { key: 'WhatsApp', value: 'WhatsApp', text: 'WhatsApp' },
    { key: 'Relatórios', value: 'Relatórios', text: 'Relatórios' },
    { key: 'Boas Práticas', value: 'Boas Práticas', text: 'Boas Práticas' },
    { key: 'Administração', value: 'Administração', text: 'Administração' }
  ];

  const ruleTypeOptions = [
    { key: 1, value: 1, text: 'Pontuação Mínima' },
    { key: 2, value: 2, text: 'Tentativas Máximas' },
    { key: 3, value: 3, text: 'Tempo Limite' }
  ];

  useEffect(() => {
    if (!hasRole('Admin')) {
      navigate('/learning');
      return;
    }

    loadTrainingTeams();

    if (isEditMode && quizId) {
      loadQuiz(quizId);
    }
  }, [hasRole, navigate, isEditMode, quizId]);

  const loadTrainingTeams = async () => {
    try {
      const teams = await teamService.getTrainingTeams();
      setAvailableTrainingTeams(teams);
    } catch (err: any) {
      console.error('Erro ao carregar times de treinamento:', err);
    }
  };

  const loadQuiz = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const quiz = await quizService.getQuiz(id);
      
      setTitle(quiz.title);
      setDescription(quiz.description || '');
      setCategory(quiz.category || '');
      setMinimumScore(quiz.minimumScore);
      setTimeLimitMinutes(quiz.timeLimitMinutes || undefined);
      setIsActive(quiz.isActive);
      
      // Converter questões
      const questionsData: QuestionForm[] = quiz.questions.map(q => {
        let options: OptionForm[] = q.options.map(opt => ({
          id: opt.id,
          text: opt.text,
          isCorrect: opt.isCorrect
        }));
        
        // Se é Múltipla Escolha mas tem opções de V/F, corrigir
        if (q.type === QuestionType.MultipleChoice && options.length === 2) {
          const hasVerdadeiroFalso = options.some(opt => 
            opt.text === 'Verdadeiro' || opt.text === 'Falso' || 
            opt.text.toLowerCase() === 'verdadeiro' || opt.text.toLowerCase() === 'falso'
          );
          
          if (hasVerdadeiroFalso) {
            // Resetar opções para vazias (sem id porque são novas)
            options = [
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
              { text: '', isCorrect: false }
            ];
          }
        }
        
        return {
          id: q.id,
          text: q.text,
          type: q.type,
          points: q.points,
          order: q.order,
          options
        };
      });
      setQuestions(questionsData.sort((a, b) => a.order - b.order));
      
      // Converter regras
      setRules(quiz.rules.map(r => ({
        ruleType: r.ruleType,
        value: r.value
      })));
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar questionário');
      showError('Erro ao carregar questionário');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!title.trim()) {
        showError('O título é obrigatório');
        return;
      }

      if (questions.length === 0) {
        showError('Adicione pelo menos uma questão');
        return;
      }

      // Validar questões
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        if (!q.text.trim()) {
          showError(`A questão ${i + 1} precisa ter um texto`);
          return;
        }
        if (q.options.length < 2) {
          showError(`A questão ${i + 1} precisa ter pelo menos 2 opções`);
          return;
        }
        if (q.type === QuestionType.TrueFalse && q.options.length !== 2) {
          showError(`A questão ${i + 1} (Verdadeiro/Falso) precisa ter exatamente 2 opções`);
          return;
        }
        const hasCorrect = q.options.some(opt => opt.isCorrect);
        if (!hasCorrect) {
          showError(`A questão ${i + 1} precisa ter pelo menos uma opção correta`);
          return;
        }
      }

      setSaving(true);
      setError(null);

      const questionsData: CreateQuestionDto[] = questions.map((q, index) => ({
        text: q.text,
        type: q.type,
        points: q.points,
        order: index + 1,
        options: q.options.map(opt => ({
          text: opt.text,
          isCorrect: opt.isCorrect
        }))
      }));

      if (isEditMode && quizId) {
        // Atualizar questionário existente
        await quizService.updateQuiz(quizId, {
          title,
          description: description || undefined,
          category: category || undefined,
          minimumScore,
          timeLimitMinutes: timeLimitMinutes || undefined,
          isActive,
          trainingTeamIds: trainingTeamIds.length > 0 ? trainingTeamIds : undefined
        });

        // TODO: Atualizar questões e regras separadamente se necessário
        showSuccess('Questionário atualizado com sucesso!');
      } else {
        // Criar novo questionário
        const createData: CreateQuizDto = {
          title,
          description: description || undefined,
          category: category || undefined,
          minimumScore,
          timeLimitMinutes: timeLimitMinutes || undefined,
          questions: questionsData,
          rules,
          trainingTeamIds: trainingTeamIds.length > 0 ? trainingTeamIds : undefined
        };

        await quizService.createQuiz(createData);
        showSuccess('Questionário criado com sucesso!');
      }

      navigate('/learning/quizzes/manage');
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar questionário');
      showError(err.message || 'Erro ao salvar questionário');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!quizId) return;

    try {
      await quizService.deleteQuiz(quizId);
      showSuccess('Questionário deletado com sucesso!');
      navigate('/learning/quizzes/manage');
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar questionário');
      showError(err.message || 'Erro ao deletar questionário');
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const addQuestion = () => {
    const newQuestion: QuestionForm = {
      text: '',
      type: QuestionType.MultipleChoice,
      points: 1,
      order: questions.length + 1,
      options: [
        { id: undefined, text: '', isCorrect: false },
        { id: undefined, text: '', isCorrect: false },
        { id: undefined, text: '', isCorrect: false },
        { id: undefined, text: '', isCorrect: false }
      ]
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index: number, field: keyof QuestionForm, value: any) => {
    const updated = [...questions];
    if (field === 'options') {
      updated[index].options = value;
    } else {
      (updated[index] as any)[field] = value;
    }
    setQuestions(updated);
  };

  const removeQuestion = (index: number) => {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  const addOption = (questionIndex: number) => {
    const updated = [...questions];
    updated[questionIndex].options.push({ id: undefined, text: '', isCorrect: false });
    setQuestions(updated);
  };

  const updateOption = (questionIndex: number, optionIndex: number, field: keyof OptionForm, value: any) => {
    const updated = [...questions];
    (updated[questionIndex].options[optionIndex] as any)[field] = value;
    setQuestions(updated);
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuestions(updated);
  };

  const handleQuestionTypeChange = (questionIndex: number, newType: QuestionType) => {
    const updated = [...questions];
    const oldType = updated[questionIndex].type;
    updated[questionIndex].type = newType;
    
    // Se mudou para Verdadeiro/Falso, ajustar opções
    if (newType === QuestionType.TrueFalse) {
      updated[questionIndex].options = [
        { id: undefined, text: 'Verdadeiro', isCorrect: false },
        { id: undefined, text: 'Falso', isCorrect: false }
      ];
    } else if (newType === QuestionType.MultipleChoice) {
      // Se mudou para Múltipla Escolha, garantir que tem opções editáveis
      // Se tinha apenas 2 opções (provavelmente era V/F), resetar para 4 opções vazias
      if (oldType === QuestionType.TrueFalse || 
          (updated[questionIndex].options.length === 2 && 
           (updated[questionIndex].options[0].text === 'Verdadeiro' || 
            updated[questionIndex].options[0].text === 'Falso'))) {
        updated[questionIndex].options = [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ];
      } else if (updated[questionIndex].options.length < 2) {
        // Se tem menos de 2 opções, garantir pelo menos 2
        while (updated[questionIndex].options.length < 2) {
          updated[questionIndex].options.push({ text: '', isCorrect: false });
        }
      }
    }
    
    setQuestions(updated);
  };

  const addRule = () => {
    setRules([...rules, { ruleType: 1, value: '' }]);
  };

  const updateRule = (index: number, field: keyof CreateQuizRuleDto, value: any) => {
    const updated = [...rules];
    (updated[index] as any)[field] = value;
    setRules(updated);
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  if (!hasRole('Admin')) {
    return null;
  }

  if (loading) {
    return (
      <GlassPageWrapper title={isEditMode ? 'Editar Questionário' : 'Novo Questionário'} icon="edit">
        <Message info>
          <Icon name="spinner" loading />
          Carregando questionário...
        </Message>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title={isEditMode ? 'Editar Questionário' : 'Novo Questionário'} icon="edit">
      {error && (
        <Message negative>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Form onSubmit={(e) => e.preventDefault()}>
        <Segment>
          <Header as="h3">Informações Básicas</Header>
          
          <Form.Field required>
            <label>Título</label>
            <Input
              placeholder="Digite o título do questionário"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Field>

          <Form.Field>
            <label>Descrição</label>
            <TextArea
              placeholder="Digite uma descrição para o questionário"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </Form.Field>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Categoria</label>
              <Dropdown
                placeholder="Selecione a categoria"
                selection
                clearable
                options={categoryOptions}
                value={category}
                onChange={(e, { value }) => setCategory(value as string)}
              />
            </Form.Field>

            <Form.Field required>
              <label>Pontuação Mínima (%)</label>
              <Input
                type="number"
                min="0"
                max="100"
                value={minimumScore}
                onChange={(e) => setMinimumScore(Number(e.target.value))}
              />
            </Form.Field>

            <Form.Field>
              <label>Tempo Limite (minutos)</label>
              <Input
                type="number"
                min="1"
                placeholder="Opcional"
                value={timeLimitMinutes || ''}
                onChange={(e) => setTimeLimitMinutes(e.target.value ? Number(e.target.value) : undefined)}
              />
            </Form.Field>
          </Form.Group>

          {isEditMode && (
            <Form.Field>
              <Checkbox
                label="Questionário Ativo"
                checked={isActive}
                onChange={(e, { checked }) => setIsActive(checked as boolean)}
              />
            </Form.Field>
          )}

            <Form.Field>
              <label>Times de Treinamento</label>
              <Dropdown
                placeholder="Selecione os times de treinamento para este questionário (opcional)"
              fluid
              multiple
              selection
              search
              options={availableTrainingTeams.map(team => ({
                key: team.id,
                text: team.name,
                value: team.id
              }))}
              value={trainingTeamIds}
              onChange={(e, { value }) => setTrainingTeamIds(value as string[])}
            />
            <div style={{ color: '#666', fontSize: '0.9em', marginTop: '5px' }}>
              Selecione os times de treinamento que terão acesso a este questionário. Se nenhum time for selecionado, o questionário ficará disponível para todos os times de treinamento.
            </div>
          </Form.Field>
        </Segment>

        <Segment>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <Header as="h3">Questões ({questions.length})</Header>
          </div>

          {questions.length === 0 ? (
            <Message info>
              <Message.Header>Nenhuma questão adicionada</Message.Header>
              <p>Clique em "Adicionar Questão" abaixo para começar.</p>
            </Message>
          ) : (
            <Accordion styled fluid>
              {questions.map((question, qIndex) => (
                <div key={qIndex}>
                  <Accordion.Title
                    active={false}
                    index={qIndex}
                  >
                    <Icon name="dropdown" />
                    <strong>Questão {qIndex + 1}:</strong> {question.text || '(Sem texto)'} 
                    <Label color={question.type === QuestionType.MultipleChoice ? 'blue' : 'green'} style={{ marginLeft: '10px' }}>
                      {question.type === QuestionType.MultipleChoice ? 'Múltipla Escolha' : 'Verdadeiro/Falso'}
                    </Label>
                    <Label color="grey" style={{ marginLeft: '5px' }}>
                      {question.points} ponto{question.points !== 1 ? 's' : ''}
                    </Label>
                  </Accordion.Title>
                  <Accordion.Content active={true}>
                    <Form>
                      <Form.Field required>
                        <label>Texto da Questão</label>
                        <TextArea
                          placeholder="Digite a pergunta"
                          value={question.text}
                          onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                          rows={2}
                        />
                      </Form.Field>

                      <Form.Group widths="equal">
                        <Form.Field required>
                          <label>Tipo</label>
                          <Dropdown
                            selection
                            options={questionTypeOptions}
                            value={question.type}
                            onChange={(e, { value }) => handleQuestionTypeChange(qIndex, value as QuestionType)}
                          />
                        </Form.Field>

                        <Form.Field required>
                          <label>Pontos</label>
                          <Input
                            type="number"
                            min="0.5"
                            step="0.5"
                            value={question.points}
                            onChange={(e) => updateQuestion(qIndex, 'points', Number(e.target.value))}
                          />
                        </Form.Field>
                      </Form.Group>

                      <Divider />

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <Header as="h4">Opções de Resposta ({question.options.length})</Header>
                        {question.type === QuestionType.MultipleChoice && (
                          <Button size="small" icon="plus" content="Adicionar Opção" onClick={() => addOption(qIndex)} />
                        )}
                      </div>

                      {question.type === QuestionType.TrueFalse ? (
                        // Interface simplificada para Verdadeiro/Falso
                        <div style={{ marginTop: '15px' }}>
                          <Form.Group widths="equal">
                            {question.options.map((option, oIndex) => {
                              const isVerdadeiro = oIndex === 0;
                              return (
                                <Form.Field key={oIndex}>
                                  <div style={{ 
                                    padding: '15px', 
                                    border: `2px solid ${option.isCorrect ? '#21ba45' : '#e0e0e0'}`,
                                    borderRadius: '8px',
                                    backgroundColor: option.isCorrect ? '#f0f9f0' : '#fafafa',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    textAlign: 'center'
                                  }}
                                  onClick={() => {
                                    const updated = [...questions];
                                    updated[qIndex].options.forEach((opt, idx) => {
                                      opt.isCorrect = idx === oIndex;
                                    });
                                    setQuestions(updated);
                                  }}
                                  >
                                    <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '8px' }}>
                                      {isVerdadeiro ? '✓ Verdadeiro' : '✗ Falso'}
                                    </div>
                                    <Checkbox
                                      label="Resposta Correta"
                                      checked={option.isCorrect}
                                      onChange={(e, { checked }) => {
                                        if (checked) {
                                          const updated = [...questions];
                                          updated[qIndex].options.forEach((opt, idx) => {
                                            opt.isCorrect = idx === oIndex;
                                          });
                                          setQuestions(updated);
                                        }
                                      }}
                                    />
                                  </div>
                                </Form.Field>
                              );
                            })}
                          </Form.Group>
                        </div>
                      ) : (
                        // Interface normal para Múltipla Escolha
                        question.options.map((option, oIndex) => (
                          <Form.Group key={oIndex} widths="equal" style={{ marginBottom: '10px' }}>
                            <Form.Field width={10}>
                              <Input
                                placeholder="Digite a opção"
                                value={option.text}
                                onChange={(e) => updateOption(qIndex, oIndex, 'text', e.target.value)}
                              />
                            </Form.Field>
                            <Form.Field width={4}>
                              <Checkbox
                                label="Correta"
                                checked={option.isCorrect}
                                onChange={(e, { checked }) => {
                                  updateOption(qIndex, oIndex, 'isCorrect', checked);
                                }}
                              />
                            </Form.Field>
                            {question.options.length > 2 && (
                              <Form.Field width={2}>
                                <Button
                                  icon="trash"
                                  color="red"
                                  size="small"
                                  onClick={() => removeOption(qIndex, oIndex)}
                                />
                              </Form.Field>
                            )}
                          </Form.Group>
                        ))
                      )}
                      
                      {question.type === QuestionType.TrueFalse && (
                        <Message info size="small" style={{ marginTop: '15px' }}>
                          <Message.Header>Questão Verdadeiro/Falso</Message.Header>
                          <p>
                            Clique na opção correta (Verdadeiro ou Falso) ou use o checkbox para marcar. Apenas uma opção pode ser a resposta correta.
                            {!question.options.some(opt => opt.isCorrect) && (
                              <strong style={{ color: 'orange', display: 'block', marginTop: '5px' }}>
                                ⚠ Você precisa marcar qual é a resposta correta
                              </strong>
                            )}
                          </p>
                        </Message>
                      )}

                      <Button
                        color="red"
                        icon="trash"
                        content="Remover Questão"
                        onClick={() => removeQuestion(qIndex)}
                        style={{ marginTop: '10px' }}
                      />
                    </Form>
                  </Accordion.Content>
                </div>
              ))}
            </Accordion>
          )}
          
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Button primary icon="plus" content="Adicionar Questão" onClick={addQuestion} size="large" />
          </div>
        </Segment>

        <Segment>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <Header as="h3">Regras de Classificação</Header>
            <Button primary icon="plus" content="Adicionar Regra" onClick={addRule} />
          </div>

          {rules.length === 0 ? (
            <Message info>
              <Message.Header>Nenhuma regra adicionada</Message.Header>
              <p>As regras definem critérios para classificação dos trainees.</p>
            </Message>
          ) : (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Tipo</Table.HeaderCell>
                  <Table.HeaderCell>Valor</Table.HeaderCell>
                  <Table.HeaderCell>Ações</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rules.map((rule, rIndex) => (
                  <Table.Row key={rIndex}>
                    <Table.Cell>
                      <Dropdown
                        selection
                        options={ruleTypeOptions}
                        value={rule.ruleType}
                        onChange={(e, { value }) => updateRule(rIndex, 'ruleType', value)}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        placeholder="Valor da regra"
                        value={rule.value}
                        onChange={(e) => updateRule(rIndex, 'value', e.target.value)}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        icon="trash"
                        color="red"
                        size="small"
                        onClick={() => removeRule(rIndex)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Segment>

        <Segment>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => navigate('/learning/quizzes/manage')}>
              Cancelar
            </Button>
            <div>
              {isEditMode && (
                <Button
                  color="red"
                  icon="trash"
                  content="Deletar"
                  onClick={() => setDeleteConfirmOpen(true)}
                  style={{ marginRight: '10px' }}
                />
              )}
              <Button
                primary
                icon="save"
                content={isEditMode ? 'Salvar Alterações' : 'Criar Questionário'}
                onClick={handleSave}
                loading={saving}
                disabled={saving}
              />
            </div>
          </div>
        </Segment>
      </Form>

      <Confirm
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        content="Tem certeza que deseja deletar este questionário? Esta ação não pode ser desfeita."
        confirmButton="Deletar"
        cancelButton="Cancelar"
      />
    </GlassPageWrapper>
  );
};

export default QuizFormPage;

